<template>
  <div
    class="cmp-category-item ws-typography"
    :class="{ 'highlighted': isSelected, 'q-my-md': mobile }"
  >
    <div class="column items-center text-center">
      <div class="q-mb-lg">
        <img
          :src="logoSrc"
          :alt="itemTitle"
          class="item-logo"
          width="45px"
          height="45px"
        />
      </div>
      <div
        class="title-section"
        :class="[desktop ? 'q-mb-lg' : 'q-mb-md']"
      >
        <h5 class="subtle">
          {{ $t('modules.i_want_product.' + item.name + '.subtitle') }}
        </h5>
        <h5 class="title">
          {{ $t('modules.i_want_product.' + item.name + '.title') }}
        </h5>
      </div>
      <div :class="[desktop ? 'q-mb-lg' : 'q-mb-md']">
        <!-- eslint-disable vue/no-v-html -->
        <span
          class="subtitle"
          v-html="xss(
            $t('modules.i_want_product.website.period_from', {price: $n(item.prices.marketingPrice, 'currency')})
          )"
        >
        </span>
        <!--eslint-enable-->
      </div>
      <div :class="[desktop ? 'q-mb-3xl' : 'q-mb-xl']">
        <span class="note">
          {{ $t('modules.i_want_product.' + item.name + '.description') }}
        </span>
      </div>
      <div class="full-width">
        <ws-button
          :type="WsButtonEnum.Type.PRIMARY"
          :icon="isSelected ? Icon.CLOSE : undefined"
          icon-right
          full-width
          @click="updateSelected()"
        >
          {{ $t(selectedBtnLabel) + ' ' + itemTitle }}
        </ws-button>
      </div>
      <div v-if="isRecommended" class="recommended q-py-sm q-px-sm text-bold">
        <span>
          {{ $t('modules.i_want_product.website.recommended') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck -- baseline for pre-existing errors so typecheck can be turned on
import {computed, defineComponent, inject, ref, watch, type PropType} from 'vue';
import { WsButton, WsButtonEnum } from '@odin/components-vue2';
import wordpressNice from '@/assets/images/wordpress-nice.svg';
import loopiaLogo from '@/assets/images/loopia-logo.svg';
import woocommerce from '@/assets/images/woocommerce.svg';
import { xss } from '@/utilities/xss';
import type {CategoryItemConfig} from '@/modules/iwp/components/items/ProductItem';
import type {SelectedProduct} from '@/modules/iwp/views/Product';
import config from '@/services/config.service';
import {CompanyId} from '@odin/utils';
import { useI18n } from '@odin/services';
import { Icon } from '@odin/assets';

export default defineComponent({
  name: 'ProductItem',
  components: {WsButton},
  props:  {
    item: { type: Object as PropType<CategoryItemConfig>, default: () => {}},
  },
  setup (props) {
    const {t} = useI18n();

    const itemTitle = computed(() => t('modules.i_want_product.' + props.item?.name + '.title'));
    const selectedProduct = inject('iwpProvider') as SelectedProduct;
    const currentlySelectedProduct = selectedProduct.selectedProduct || ref('');
    const updateSelectedProduct = selectedProduct.updateSelectedProduct;


    const isSelected = ref(false);
    const isRecommended = ref(props.item.name.includes('siteBuilder'));
    const logoA24 = 'https://cdn.active24.eu/logos/a24-symbol-green.svg';
    const getLogo = () => {
      if (config.company === CompanyId.ACTIVE_24_CZ) {
        return logoA24;
      } else {
        return loopiaLogo;
      }
    };

    const logoSrc = computed(() => {
      if (props.item?.name.includes('siteBuilder')) {
        return getLogo();
      }
      return props.item?.name.includes('nonEcommerce') ? wordpressNice : woocommerce;
    });

    const selectedBtnLabel = computed(() => {
      return isSelected.value ?
        'modules.i_want_product.website.remove_btn_label' :
        'modules.i_want_product.website.select_btn_label';
    });

    const updateSelected = () => {
      updateSelectedProduct(props.item?.name);
    };

    watch(currentlySelectedProduct, () => {
      isSelected.value = props.item.name === currentlySelectedProduct.value.toString();
    });

    return {
      Icon,
      isRecommended,
      isSelected,
      itemTitle,
      logoSrc,
      selectedBtnLabel,
      updateSelected,
      WsButtonEnum,
    };
  },
  methods: {
    xss,
  },
});
</script>

<style scoped lang="scss">
.cmp-category-item {
  position: relative;
  background: $iwp-background;
  border: $iwp-border;
  border-radius: $iwp-border-radius;
  box-shadow: $ws-box-shadow7;
  padding: map-get($space-3xl, y) map-get($space-lg, x)
    map-get($space-lg, y) map-get($space-lg, x);

  display: flex;
  flex-direction: column;

  .item-logo {
    width: auto;
    height: 45px;
    min-width: 45px;
    min-height: 45px;
  }

  .title-section {
    .title {
      color: $iwp-title-color !important;
    }
    .subtle {
      color: $iwp-subtitle-color;
    }
  }

  .subtitle {
    color: $iwp-subtitle-color;
    .price {
      color: $iwp-subtitle-price-color;
    }
  }

  .note {
    color: $iwp-note-color;
  }

  &.highlighted {
    background: $iwp-highlight-bg;
    color: $iwp-highlight-color;

    .title-section {
      .title {
        color: $iwp-highlight-color !important;
      }
      .subtle {
        color: $iwp-highlight-subtitle-color;
      }
    }

    .subtitle {
      color: $iwp-highlight-subtitle-color;
      .price {
        color: $iwp-highlight-color;
      }
    }

    .note {
      color: $iwp-highlight-note-color;
    }

    div > div > button.q-btn.ws-button {
      &:not([class*=' text-']) {
        color: $toggle-button-active-color;
      }

      &.primary {
        &:before {
          background-color: $toggle-button-active-tab-bg;
        }

        &.darken-on-hover {
          background-color: $button-primary-bg-hover;
          transition: background-color 150ms;
          &:hover {
            background-color: #0423D0 !important;
          }
        }
      }
    }
  }

  .recommended {
    position: absolute;
    height: 34px;
    left: 50%;
    top: -17px;
    background: $iwp-recommended-bg;
    color: $iwp-recommended-color;
    transform: translate(-50%);
    border-radius: 8px;
  }
}
</style>
